import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Observable, of, map, switchMap, tap } from 'rxjs';

import { CmsService, Page, PageMetaService } from '@spartacus/core';

import { ArgumentsLayoutEnum, IArgumentWithConfig } from '@seb/web-design-system/arguments';
import { SdArgumentsSliderComponent } from '@seb/web-design-system/arguments-slider';
import { BrandsEnum } from '@seb/web-design-system/core';

import { argumentsSkeleton, ArgumentsSliderModuleInterface } from '../../../models';
import { AbstractModuleComponent } from '../abstract-module.component';
import { ChatService, HobStateService } from '../../../services';
import { ArgumentMapper } from '../../../mappers';

@Component({
  selector: 'wc-arguments-slider-module',
  templateUrl: './arguments-slider-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdArgumentsSliderComponent]
})
export class ArgumentsSliderModuleComponent extends AbstractModuleComponent<ArgumentsSliderModuleInterface> implements OnInit, OnDestroy {
  layout: ArgumentsLayoutEnum = ArgumentsLayoutEnum.default;
  arguments: IArgumentWithConfig[] = [];
  context: string;
  activeBrand: BrandsEnum;
  isHob: boolean;

  constructor(
    private argumentMapper: ArgumentMapper,
    private cdRef: ChangeDetectorRef,
    private pageMetaService: PageMetaService,
    private cmsService: CmsService,
    private readonly chatService: ChatService,
    private readonly hobStateService: HobStateService
  ) {
    super();
  }

  override onModuleInit(module: ArgumentsSliderModuleInterface) {
    if (this.useLazyLoad) {
      this.arguments = new Array(module.components?.split(' ').length).fill(undefined).map(
        () => ({ ...argumentsSkeleton }) as IArgumentWithConfig
      );
    }
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.hobStateService.hobState$.subscribe(({ isHob, liveBrand, siteBrand }) => {
        this.activeBrand = liveBrand || siteBrand;
        this.isHob = isHob;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.module$.pipe(
        switchMap((module) =>
          this.componentService.getSubComponentsLazyComponentData(module, 'componentsObjects', 'links')
        ),
        tap((argumentsSliderModule: ArgumentsSliderModuleInterface) => {
          if (argumentsSliderModule.componentsObjects?.length > 0) {
            this.layout = this.argumentMapper.mapLayout(argumentsSliderModule.componentsObjects[0].layout);
          }
        }),
        switchMap((argumentsSliderModule: ArgumentsSliderModuleInterface) => {
          if (argumentsSliderModule.componentsObjects?.length > 0) {
            return this.cmsService.getCurrentPage().pipe(
              switchMap((page) => {
                const arguments$: Observable<IArgumentWithConfig>[] = argumentsSliderModule.componentsObjects
                  .filter((argument) => argument.layout === argumentsSliderModule.componentsObjects[0].layout)
                  .map((argument) => {
                    if (this.brandCorner) {
                      argument['brandCorner'] = this.brandCorner;
                    }
                    return this.argumentMapper.argumentsComponentToIArgumentWithConfig(argument).pipe(
                      map((mappedArgument) => {
                        mappedArgument.contentData.hasAlphaHeader = this.checkAlphaHeader(page);
                        return mappedArgument;
                      }));
                  });
                return combineLatest(arguments$);
              }));
          } else {
            return of([]);
          }
        })
      ).subscribe((iArguments) => {
        this.arguments = iArguments;
        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.pageMetaService.getMeta().subscribe((pageMeta) => {
        this.context = this.wrapper?.wrapperHeading?.headline
          ? `${pageMeta.title} ${this.wrapper?.wrapperHeading?.headline}`
          : pageMeta.title;
      })
    );
  }

  onClicked(event): void {
    if (event.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
  }

  checkAlphaHeader(page: Page): boolean {
    return page['headerDesign'] ? page['headerDesign'] === 'ALPHA' && this.layout === ArgumentsLayoutEnum.embedded : false;
  }
}
